import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserItems, getItemsByTag, updateItemFlags } from '../services/items';
import { searchItems, type SearchResult } from '../services/search';
import ItemsView, { ViewModeToggle, type ViewMode } from '../components/items/ItemsView';
import type { Item } from '../types';
import { ItemFlags, hasFlag } from '../types';

function searchResultToItem(result: SearchResult): Item {
  return {
    id: result.objectID,
    type: result.type as Item['type'],
    title: result.title,
    content: '',
    summary: result.summary,
    imageUrl: result.imageUrl,
    tags: result.tags ?? [],
    autoTags: result.autoTags ?? [],
    createdBy: result.createdBy,
    createdAt: new Date(),
    updatedAt: new Date(),
    folderIds: [],
    visibility: 'private',
    sharedWith: [],
    sharedWithContactIds: [],
    enrichmentStatus: 'complete',
    site: result.site || undefined,
    flags: 0,
  };
}

export default function SearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => { const stored = localStorage.getItem('itemsViewMode'); return stored === 'card' || stored === 'list' ? stored : 'card'; });
  const handleViewModeChange = (mode: ViewMode) => { setViewMode(mode); localStorage.setItem('itemsViewMode', mode); };
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  const doSearch = async (searchQuery: string, tag?: string) => {
    if (!user) return;
    setLoading(true);
    const effectiveTag = tag ?? tagFilter;

    try {
      // Tag filter: use Firestore directly
      if (effectiveTag && !searchQuery.trim()) {
        const result = await getItemsByTag(effectiveTag);
        setItems(filterByType(result));
        return;
      }

      // Text search: try Algolia, fall back to showing all items filtered client-side
      if (searchQuery.trim()) {
        try {
          const hits = await searchItems(searchQuery, {
            type: typeFilter || undefined,
            tags: effectiveTag ? [effectiveTag] : undefined,
          });
          if (hits.length > 0) {
            setItems(hits.map(searchResultToItem));
            return;
          }
        } catch {
          // Algolia failed, fall through to client-side filter
        }

        // Client-side filter as fallback
        const all = await getUserItems(user.uid);
        const lower = searchQuery.toLowerCase();
        const filtered = all.filter((i) =>
          i.title.toLowerCase().includes(lower) ||
          i.summary.toLowerCase().includes(lower) ||
          i.content.toLowerCase().includes(lower) ||
          i.tags.some((t) => t.toLowerCase().includes(lower)) ||
          i.autoTags.some((t) => t.toLowerCase().includes(lower))
        );
        setItems(filterByType(filtered));
        return;
      }

      // Empty search: show all items
      const all = await getUserItems(user.uid);
      setItems(filterByType(all));
    } finally {
      setLoading(false);
    }
  };

  const filterByType = (list: Item[]) => {
    if (!typeFilter) return list;
    return list.filter((i) => i.type === typeFilter);
  };

  useEffect(() => {
    const tag = searchParams.get('tag');
    if (tag) {
      setTagFilter(tag);
      setQuery('');
      doSearch('', tag);
    } else {
      doSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    doSearch(value);
  };

  const clearTagFilter = () => {
    setTagFilter('');
    doSearch(query, '');
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
        Search
      </Typography>

      <TextField
        fullWidth
        placeholder="Search items..."
        value={query}
        onChange={(e) => handleQueryChange(e.target.value)}
        autoFocus
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, v) => {
            setTypeFilter(v ?? '');
            doSearch(query);
          }}
          size="small"
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="url">URLs</ToggleButton>
          <ToggleButton value="text">Text</ToggleButton>
          <ToggleButton value="image">Images</ToggleButton>
          <ToggleButton value="file">Files</ToggleButton>
        </ToggleButtonGroup>

        {tagFilter && (
          <Chip
            label={`Tag: ${tagFilter}`}
            onDelete={clearTagFilter}
            color="primary"
            size="small"
          />
        )}

        <Box sx={{ flexGrow: 1 }} />
        <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length > 0 ? (
        <ItemsView
          items={items}
          viewMode={viewMode}
          onClick={(i) => navigate(`/items/${i.id}`)}
          onToggleStar={(i) => {
            const newFlags = hasFlag(i.flags, ItemFlags.STARRED)
              ? i.flags & ~ItemFlags.STARRED
              : i.flags | ItemFlags.STARRED;
            setItems((prev) => prev.map((it) => it.id === i.id ? { ...it, flags: newFlags } : it));
            updateItemFlags(i.id, newFlags);
          }}
          onTagClick={(tag) => { setTagFilter(tag); setQuery(''); doSearch('', tag); }}
        />
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          No results found.
        </Typography>
      )}
    </Box>
  );
}
